"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { traceFlash } from "@/lib/flash-trace";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  mounted: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("light");
  const [mounted, setMounted] = useState(false);

  // Initialize theme from localStorage (default: light)
  useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem("bash-theme") as Theme | null;
    if (stored) {
      setThemeState(stored);
    }
  }, []);

  // Apply theme to document - disable transitions during switch for instant change
  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;

    // Repainting every pixel on the page is the point here, so this seam can
    // look exactly like an unexplained whole-screen flash (§flash-trace).
    traceFlash("theme", theme);

    // Disable all transitions during theme change
    root.classList.add("disable-transitions");

    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("bash-theme", theme);

    // Force reflow then re-enable transitions
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    root.offsetHeight;
    root.classList.remove("disable-transitions");
  }, [theme, mounted]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      mounted,
    }),
    [theme, setTheme, toggleTheme, mounted]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return default values for SSR/SSG
    return {
      theme: "light" as Theme,
      setTheme: () => {},
      toggleTheme: () => {},
      mounted: false,
    };
  }
  return context;
}
